import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";

import {ActivatedRoute, Router} from "@angular/router";
import {MatRipple} from "@angular/material/core";
import {Subject} from "rxjs";
import {takeUntil} from 'rxjs/operators';
import {
  AuthControllerService,
  Organisation,
  OrganisationControllerService,
  Parameter,
  Project,
  ProjectControllerService,
  Task,
  User
} from "../../../../api";
import {CanDeactivateComponent} from "../../../../guards/unsaved-changes.guard";
import {PermissionService} from "../../../../services/permission/permission.service";
import {ToolbarService} from "../../../util/design/toolbar/toolbar.service";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {faCalendarDays, faChevronRight, faSpinnerThird} from "@fortawesome/pro-solid-svg-icons";
import { KeyValuePipe, NgClass, NgTemplateOutlet } from "@angular/common";

import {ProjectImageUploadComponent} from "../../../util/project-image-upload/project-image-upload.component";
import {UsersListComponent} from "../../../util/users/users.component";
import {AddressInputComponent, applyDefaultCountry} from "../../../util/addressInput/address-input.component";
import {ProjectKindPipe} from "../../../../pipes/project-kind.pipe";
import {
  BicAccordionBodyComponent,
  BicAccordionComponent,
  BicAccordionElementComponent,
  BicAccordionHeaderComponent,
  BicButtonComponent,
  BicDatepickerInputDirective,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicToastService,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {serverErrorMessage} from "../../../my-checks/shared/server-error-message";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {PageHeaderComponent} from "../../../util/design/page-header/page-header.component";
import {NotificationsService} from '../../../../services/notifications/notifications.service';
import {WorkflowService} from '../../../../services/workflow.service';
import {qualityParametersFromManifest} from '../../../../models/quality/quality-parameter-schema';
import {toLocalDate} from '../../../../services/date/local-date';
import SalutationEnum = User.SalutationEnum;
import {notBlankValidator, zipCodeValidator} from '../../../../models/project-validators';
import LphEnum = Task.LphEnum;

type ScheduleField = 'beginPlanning' | 'endPlanning' | 'startExecuting' | 'endExecuting';

/** Dezimalzahl mit optionalem Vorzeichen und Komma oder Punkt als Trennzeichen, z. B. „-2,50". */
const DECIMAL_INPUT_PATTERN = /^[-+]?\d+([.,]\d+)?$/;

/**
 * Validator der Dezimalfelder in den Prüfeinstellungen. Das Control hält den rohen Eingabetext;
 * leer ist erlaubt, sonst muss der Text {@link DECIMAL_INPUT_PATTERN} entsprechen.
 */
export const decimalInputValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const text = String(control.value ?? '').trim();
  return text === '' || DECIMAL_INPUT_PATTERN.test(text) ? null : {decimal: true};
};

@Component({
  selector: 'app-projectSettings',
  templateUrl: './project-settings.component.html',
  styleUrls: ['./project-settings.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProjectImageUploadComponent,
    UsersListComponent,
    AddressInputComponent,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    ProjectKindPipe,
    BicButtonComponent,
    BicDropdownElementComponent,
    BicSelectComponent,
    BicInputComponent,
    BicDatepickerInputDirective,
    BicAccordionComponent,
    BicAccordionHeaderComponent,
    BicAccordionElementComponent,
    BicAccordionBodyComponent,
    FaIconComponent,
    KeyValuePipe,
    PageHeaderComponent,
    TooltipDirective
]
})
export class ProjectSettings implements OnInit, OnDestroy, CanDeactivateComponent, AfterViewInit {
  private readonly scheduleFields: ScheduleField[] = ['beginPlanning', 'endPlanning', 'startExecuting', 'endExecuting'];
  permissionService = inject(PermissionService);
  dialog = inject(MatDialog);
  toolbar = inject(ToolbarService);
  stateConfigService = inject(StateConfigService);
  flowService = inject(WorkflowService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  private authControllerService = inject(AuthControllerService);
  private projectControllerService = inject(ProjectControllerService);
  private organisationControllerService = inject(OrganisationControllerService);
  private destroy$ = new Subject<void>();
  private isScheduleUpdateInProgress: boolean = false;
  private schedulePreviousValues = new WeakMap<FormGroup, Partial<Record<ScheduleField, Date | undefined>>>();
  notificationsService = inject(NotificationsService);

  //form groups for new view
  generalInfoForm!: FormGroup;
  addressInfoForm!: FormGroup;
  builderInfoForm!: FormGroup;
  qualitySettingsInfoForm!: FormGroup;

  salutation: typeof SalutationEnum = SalutationEnum;

  //old form groups
  ownerForm!: FormGroup;
  generalForm!: FormGroup;
  placeForm!: FormGroup;
  timeForm!: FormGroup;

  minEndExecuting?: Date;
  minEndPlanning?: Date;

  qualityParams: Parameter[][] = [];
  qualityParamsLoading: boolean = true;
  qualityValue: any = {};

  project!: Project;
  user!: User;
  projectId!: number;
  organisations: Organisation[] = [];
  organisationLoaded: boolean = false;

  create!: boolean;
  files: Map<string, File[]> = new Map<string, File[]>();
  image: File[] = [];
  editImagePermission: boolean = true;

  projectKinds: typeof Project.ProjectKindEnum = Project.ProjectKindEnum;

  phasesSelected: Map<string, boolean> = new Map<string, boolean>();

  unsubscribe = new Subject<void>();

  expandedElement: number | null = null;

  private initialFormValue: string = '';

  constructor() {
    for (const lph in LphEnum) {
      this.phasesSelected.set(lph, false);
      this.files.set(lph, []);
    }
  }

  ngAfterViewInit(): void {
    this.expandedElement = null;
  }

  isSubmitting = false;

  get createDisabled() {
    return this.isSubmitting || !this.generalForm.valid || !this.ownerForm.valid || !this.placeForm.valid || !this.timeForm.valid;
  }

  get nextDisabledStep1() {
    return !this.generalForm.valid || !this.ownerForm.valid;
  }

  get nextDisabledStep2() {
    return !this.placeForm.valid;
  }

  get saveDisabled(): boolean {
    if (this.create) {
      return true;
    }

    if (!this.generalInfoForm || !this.addressInfoForm || !this.builderInfoForm || !this.qualitySettingsInfoForm) {
      return true;
    }

    const isAnyFormInvalid =
      this.generalInfoForm.invalid ||
      this.addressInfoForm.invalid ||
      this.builderInfoForm.invalid ||
      this.qualitySettingsInfoForm.invalid;

    return !this.isDirty || isAnyFormInvalid;
  }

  /**
   * Berechtigungsprüfung für das Editieren des Projekts
   */
  get hasEditPermission(): boolean {
    if(this.stateConfigService.getOrganisationId() != 0 && this.stateConfigService.getOrganisationId() != null && !this.stateConfigService.isPersonal){
      if(this.permissionService.hasOrganisationPrivilege('ORGANISATION_PROJECT_EDIT')){
        return true;
      }else{
        return this.permissionService.hasProjectPrivilege('PROJECT_SETTINGS');
      }
    }else{
      return this.permissionService.hasProjectPrivilege('PROJECT_SETTINGS');
    }
  }

  /**
   * Mapped auf hasEditPermission, damit HTML und TS synchron laufen
   */
  get hasSettingsEditPermission(): boolean {
    return this.hasEditPermission;
  }

  get noPermissionTooltip(): string | undefined {
    return !this.hasEditPermission ? 'Keine Berechtigung' : undefined;
  }

  get canDeleteProject(): boolean {
    if (!this.project) {
      return false;
    }

    const orgId = (this.project as any).organisationId;

    const isOrgAdmin = this.permissionService.hasOrganisationPrivilege(
      'ORGANISATION_PROJECT_DELETE',
      orgId
    ) || this.permissionService.hasOrganisationPrivilege(
      'ORGANISATION_SETTINGS',
      orgId
    );

    if (isOrgAdmin) {
      return true;
    }

    const currentUserId = this.user?.id || this.stateConfigService.user?.id;

    const isCreator = currentUserId !== undefined && this.project.creator?.id === currentUserId;

    const isProjectAdmin = this.permissionService.hasProjectPrivilege(
      'PROJECT_SETTINGS',
      this.project.id
    );

    return isCreator || isProjectAdmin;
  }
  /**
   * Liefert den Tooltip-Text für den Löschen-Button, falls keine Berechtigung vorliegt
   */
  get deleteNoPermissionTooltip(): string | undefined {
    return !this.canDeleteProject ? 'Keine Berechtigung zum Löschen des Projekts' : undefined;
  }

  public snapshotInitialState(): void {
    this.initialFormValue = this.serializeState();
  }

  /**
   * Serialisiert den bearbeitbaren Zustand fuer den Dirty-Vergleich.
   *
   * Die vier Terminfelder werden dabei auf den angezeigten Kalendertag reduziert:
   * aus API/localStorage kommen sie als String bzw. Zahl, die Datepicker schreiben
   * aber Date-Objekte zurueck (siehe (dateChange) im Template). JSON.stringify machte
   * daraus "2025-01-01" vs. "2025-01-01T00:00:00.000Z", womit die Seite dauerhaft als
   * geaendert galt, obwohl der Nutzer nichts angefasst hatte.
   */
  private serializeState(): string {
    return JSON.stringify({
      general: this.generalInfoForm?.value,
      address: this.addressInfoForm?.value,
      builder: this.normalizeScheduleValues(this.builderInfoForm?.value),
      qualityForm: this.qualityFormState(),
      qualityObj: this.qualityValue
    });
  }

  /**
   * Wert des Prüfeinstellungs-Formulars für den Dirty-Vergleich. Dezimalfelder halten den rohen
   * Eingabetext; eine gültige Eingabe steht bereits als Zahl in qualityValue und zählt nur dort.
   * Sonst gälte „-2,50" statt „-2,5" als Änderung, und ein erst beim Tippen angelegtes Control
   * verschöbe den Vergleichsstand. Ungültiger Text zählt dagegen als ungespeicherte Änderung.
   */
  private qualityFormState(): Record<string, unknown> | undefined {
    const form = this.qualitySettingsInfoForm;
    if (!form) {
      return undefined;
    }
    const state: Record<string, unknown> = {...form.value};
    for (const [key, control] of Object.entries(form.controls)) {
      if (control.hasValidator(decimalInputValidator) && control.valid) {
        delete state[key];
      }
    }
    return state;
  }

  private normalizeScheduleValues(formValue: any): any {
    if (!formValue) {
      return formValue;
    }
    const normalized = {...formValue};
    for (const field of this.scheduleFields) {
      if (field in normalized) {
        normalized[field] = this.toCalendarDay(normalized[field]);
      }
    }
    return normalized;
  }

  /** Reduziert einen Datumswert auf den lokal angezeigten Tag (YYYY-MM-DD). */
  private toCalendarDay(value: unknown): string | undefined {
    const date = this.toDate(value);
    if (!date) {
      return undefined;
    }
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /**
   * Setzt alle Formulare auf den geladenen Projektstand zurueck. Vom UnsavedChangesGuard
   * beim Verwerfen aufgerufen - ohne diese Methode lief der Guard in seinen Fallback und
   * schrieb auf den Getter isDirty, was die Navigation dauerhaft blockierte.
   */
  public discardChanges(): void {
    const project = this.stateConfigService.selectedProject;
    if (!project) {
      return;
    }

    for (const form of [this.generalInfoForm, this.addressInfoForm, this.builderInfoForm, this.qualitySettingsInfoForm]) {
      if (!form) {
        continue;
      }
      form.reset(undefined, {emitEvent: false});
      form.patchValue(project, {emitEvent: false});
      form.markAsPristine();
      form.markAsUntouched();
    }

    this.qualityValue = JSON.parse(project.qualitySettings ?? "{}");

    if (this.builderInfoForm) {
      this.rememberScheduleValues(this.builderInfoForm);
      this.updateScheduleDateConstraints(this.builderInfoForm);
    }
    if (this.addressInfoForm) {
      applyDefaultCountry(this.addressInfoForm.get('country'));
      this.onCountryChange(this.addressInfoForm.get('country')?.value);
    }

    this.snapshotInitialState();
  }

  disableForm(): boolean {
    return false;
  }

  canDeactivate(): boolean {
    if (this.create) {
      return true;
    }
    return !this.isDirty;
  }

  validForm(): boolean {
    if(this.create) {
      return true;
    }

    // For edit mode, check new forms
    if (this.generalInfoForm && this.addressInfoForm && this.builderInfoForm && this.qualitySettingsInfoForm) {
      return (this.generalInfoForm.valid || this.generalInfoForm.disabled) &&
        (this.addressInfoForm.valid || this.addressInfoForm.disabled) &&
        (this.builderInfoForm.valid || this.builderInfoForm.disabled) &&
        (this.qualitySettingsInfoForm.valid || this.qualitySettingsInfoForm.disabled);
    }
    return (this.generalForm.valid) && (this.placeForm.valid) && (this.timeForm.valid);
  }

  get isDirty(): boolean {
    if (this.create) {
      return false;
    }

    return this.serializeState() !== this.initialFormValue;
  }

  onCountryChange(country: string) {
    const stateControl = this.addressInfoForm?.get('state');
    if (country === 'DEU' && this.hasEditPermission) {
      stateControl?.enable();
    } else {
      stateControl?.disable();
      stateControl?.reset();
    }
  }

  createProject(): void {
    if (this.isSubmitting || !this.validateScheduleBeforeSave(this.timeForm)) {
      return;
    }

    this.project = {...this.project, ...this.generalForm.value, ...this.placeForm.value, ...this.timeForm.value};
    let owner = this.ownerForm.value["ownerOrganisation"] != -1 ? this.ownerForm.value["ownerOrganisation"] : undefined;

    this.isSubmitting = true;
    this.projectControllerService.createProject({project: this.project, organisationId: owner}).subscribe({
      next: () => {
        this.generalForm.markAsPristine();
        this.placeForm.markAsPristine();
        this.timeForm.markAsPristine();
        void this.router.navigateByUrl("/projects");
        this.stateConfigService.update().subscribe();
      },
      error: () => {
        this.isSubmitting = false;
        this.notificationsService.showErrorMessage('Projekt konnte nicht erstellt werden. Bitte erneut versuchen.');
      }
    });
  }

  save(): void {
    if(!this.create) {
      this.generalInfoForm?.markAllAsTouched();
      this.addressInfoForm?.markAllAsTouched();
      this.builderInfoForm?.markAllAsTouched();
      this.qualitySettingsInfoForm?.markAllAsTouched();

      if (this.generalInfoForm?.invalid || this.addressInfoForm?.invalid || this.builderInfoForm?.invalid || this.qualitySettingsInfoForm?.invalid) {
        this.notificationsService.showErrorMessage('Bitte alle Pflichtfelder korrekt ausfüllen!');
        return;
      }
      this.saveWith(true);
    }
  }

  /**
   * Wert eines Zeitplan-Felds als Datum. Das Datumsfeld meldet neben `Date` auch den Feldtext
   * (`TT.MM.JJJJ`); `new Date` würde den als Monat.Tag lesen (siehe {@link toLocalDate}).
   */
  private toDate(val: unknown): Date | undefined {
    return toLocalDate(val) ?? undefined;
  }

  private getScheduleValues(form: FormGroup): Record<ScheduleField, Date | undefined> {
    return {
      beginPlanning: this.toDate(form.get('beginPlanning')?.value),
      endPlanning: this.toDate(form.get('endPlanning')?.value),
      startExecuting: this.toDate(form.get('startExecuting')?.value),
      endExecuting: this.toDate(form.get('endExecuting')?.value),
    };
  }

  private getScheduleValidationError(
    values: Record<ScheduleField, Date | undefined>
  ): { fields: ScheduleField[]; message: string } | undefined {
    if (values.beginPlanning && values.endPlanning && values.beginPlanning > values.endPlanning) {
      return {fields: ['beginPlanning', 'endPlanning'], message: 'Planungsbeginn darf nicht nach dem Planungsende liegen.'};
    }
    if (values.startExecuting && values.beginPlanning && values.startExecuting < values.beginPlanning) {
      return {fields: ['startExecuting', 'beginPlanning'], message: 'Ausführungsbeginn darf nicht vor dem Planungsbeginn liegen.'};
    }
    if (values.startExecuting && values.endExecuting && values.startExecuting > values.endExecuting) {
      return {fields: ['startExecuting', 'endExecuting'], message: 'Ausführungsbeginn darf nicht nach dem Ausführungsende liegen.'};
    }
    return undefined;
  }

  private rememberScheduleValues(form: FormGroup): void {
    this.schedulePreviousValues.set(form, this.getScheduleValues(form));
  }

  private updateScheduleDateConstraints(form: FormGroup): void {
    const values = this.getScheduleValues(form);
    this.minEndPlanning = values.beginPlanning;
    this.minEndExecuting = values.startExecuting;
  }

  private applyScheduleDefaults(form: FormGroup, changedField: ScheduleField): void {
    if (changedField !== 'endPlanning') {
      return;
    }
    const values = this.getScheduleValues(form);
    if (values.endPlanning && !values.startExecuting) {
      const proposedValues = {...values, startExecuting: values.endPlanning};
      if (!this.getScheduleValidationError(proposedValues)) {
        form.get('startExecuting')?.setValue(values.endPlanning, {emitEvent: false});
      }
    }
  }

  private handleScheduleFieldChange(form: FormGroup, changedField: ScheduleField): void {
    if (this.isScheduleUpdateInProgress) {
      return;
    }

    this.isScheduleUpdateInProgress = true;
    this.applyScheduleDefaults(form, changedField);
    this.isScheduleUpdateInProgress = false;

    this.updateScheduleDateConstraints(form);
    this.revalidateSchedule(form);
  }

  scheduleErrorMessage(form: FormGroup | undefined): string | null {
    for (const field of this.scheduleFields) {
      const message = form?.get(field)?.getError('scheduleConflict');
      if (typeof message === 'string') {
        return message;
      }
    }
    return null;
  }

  private revalidateSchedule(form: FormGroup): void {
    for (const field of this.scheduleFields) {
      const control = form.get(field);
      if (control?.hasError('scheduleConflict')) {
        const {scheduleConflict, ...rest} = control.errors ?? {};
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
    }

    const error = this.getScheduleValidationError(this.getScheduleValues(form));
    if (!error) {
      return;
    }

    for (const field of error.fields) {
      const control = form.get(field);
      control?.setErrors({...(control.errors ?? {}), scheduleConflict: error.message});
    }

    this.notificationsService.showErrorMessage(error.message);
  }

  private setupScheduleValidation(form: FormGroup): void {
    this.updateScheduleDateConstraints(form);
    for (const field of this.scheduleFields) {
      form.get(field)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.handleScheduleFieldChange(form, field));
    }
  }

  private validateScheduleBeforeSave(form: FormGroup): boolean {
    this.revalidateSchedule(form);
    return !this.getScheduleValidationError(this.getScheduleValues(form));
  }

  ngOnInit(): void {
    this.organisationControllerService.getOrganisations().subscribe(organisations => {
      // Nur Organisationen anzeigen, in denen der Nutzer auch Projekte erstellen/bearbeiten darf:
      this.organisations = organisations.filter(
        org => this.permissionService.hasOrganisationPrivilege('ORGANISATION_PROJECT_EDIT', org.id)
      );
      this.organisationLoaded = true;
    });

    this.authControllerService.profile().subscribe(user => {
      this.user = user;
    });

    // Feldliste der Prüfeinstellungen aus dem Manifest des Moduls `quality_check` (Workflow API).
    // Vorher kam sie aus `GET /quality/rules`, also aus der Legacy-Quality-API — das Panel wäre mit
    // deren Abschaltung leer gewesen. Dasselbe Manifest treibt die Felder im Regel-Editor, Katalog
    // und Editor können also nicht auseinanderlaufen.
    this.flowService.listModules().subscribe({
      next: modules => {
        const params = qualityParametersFromManifest(modules);
        this.qualityParams = Array.from({length: Math.ceil(params.length / 4)}, (_, i) => params.slice(i * 4, i * 4 + 4));
        this.qualityParamsLoading = false;
      },
      error: () => {
        this.qualityParamsLoading = false;
      }
    });

    this.route.parent!.params.subscribe(params => {
      this.create = params.id === undefined;
      this.projectId = params.id;

      let defaultOwner: number | undefined;
      if (this.stateConfigService.isPersonal) {
        defaultOwner = -1;
      } else {
        const orgId = this.stateConfigService.getOrganisationIdOrUndefined();
        if (orgId) {
          defaultOwner = orgId;
        }
      }

      this.ownerForm = this.formBuilder.group({
        ownerOrganisation: new FormControl(defaultOwner, [Validators.required]),
      });

      this.generalForm = this.formBuilder.group({
        id: new FormControl(),
        name: new UntypedFormControl("", [Validators.required, notBlankValidator]),
        software: new UntypedFormControl(undefined),
        projectIdIntern: new UntypedFormControl(undefined),
        projectKind: new UntypedFormControl(undefined),
        isBIM: new UntypedFormControl(false, [Validators.required])
      });

      this.placeForm = this.formBuilder.group({
        buildingPlace: new UntypedFormControl("", [Validators.required]),
        plotNumber: new UntypedFormControl(""),
        street: new UntypedFormControl(""),
        zipCode: new UntypedFormControl("", [Validators.required, zipCodeValidator()]),
        houseNumber: new UntypedFormControl(""),
        country: new UntypedFormControl("", [Validators.required]),
        state: new UntypedFormControl(undefined, [Validators.required]),
      });
      // Projekt erstellen: Deutschland vorbelegen; im Edit-Pfad gewinnt das gespeicherte Land beim Patchen unten.
      applyDefaultCountry(this.placeForm.get('country'));

      this.timeForm = this.formBuilder.group({
        beginPlanning: new UntypedFormControl(undefined),
        endPlanning: new UntypedFormControl(undefined),
        startExecuting: new UntypedFormControl(undefined),
        endExecuting: new UntypedFormControl(undefined),
      });
      this.setupScheduleValidation(this.timeForm);

      if(!this.create) {
        this.generalInfoForm = this.formBuilder.group({
          name: new UntypedFormControl("", [Validators.required, notBlankValidator]),
          projectIdIntern: new FormControl(undefined),
          projectKind: new UntypedFormControl(undefined),
          isBIM: new UntypedFormControl(false, [Validators.required]),
          software: new UntypedFormControl(undefined),
        });

        this.addressInfoForm = this.formBuilder.group({
          street: new UntypedFormControl("", [Validators.required]),
          houseNumber: new UntypedFormControl(""),
          plotNumber: new UntypedFormControl(""),
          zipCode: new UntypedFormControl("", [Validators.required, zipCodeValidator()]),
          buildingPlace: new UntypedFormControl("", [Validators.required]),
          country: new UntypedFormControl("", [Validators.required]),
          state: new UntypedFormControl("", [Validators.required]),
        });

        this.builderInfoForm = this.formBuilder.group({
          builderSalutation: new UntypedFormControl(undefined),
          builderFirstName: new UntypedFormControl({value: this.stateConfigService.selectedProject.builderFirstName}),
          builderLastName: new UntypedFormControl({value: this.stateConfigService.selectedProject.builderLastName}),
          beginPlanning: new UntypedFormControl(undefined),
          endPlanning: new UntypedFormControl(undefined),
          startExecuting: new UntypedFormControl(undefined),
          endExecuting: new UntypedFormControl(undefined),
          software: new UntypedFormControl(undefined),
        });

        this.qualitySettingsInfoForm = this.formBuilder.group({
          workingPosture: new UntypedFormControl(""),
          workingLoad: new UntypedFormControl(""),
          numberOfEmployees: new UntypedFormControl(""),
          groundwater_level: new UntypedFormControl("", [decimalInputValidator]),
          isFreestanding: new UntypedFormControl(""),
        });

        this.generalInfoForm.patchValue(this.stateConfigService.selectedProject);
        this.addressInfoForm.patchValue(this.stateConfigService.selectedProject);
        this.builderInfoForm.patchValue(this.stateConfigService.selectedProject);
        this.qualitySettingsInfoForm.patchValue(this.stateConfigService.selectedProject);

        // Erst nach dem Befuellen verdrahten: applyScheduleDefaults() wuerde sonst
        // beim Patchen von endPlanning gleich startExecuting mitsetzen und den
        // Vergleichsstand gegen die geladenen Projektdaten verschieben.
        this.setupScheduleValidation(this.builderInfoForm);

        // Formulare read-only schalten bei fehlenden Rechten
        if (!this.hasEditPermission) {
          this.generalInfoForm.disable({emitEvent: false});
          this.addressInfoForm.disable({emitEvent: false});
          this.builderInfoForm.disable({emitEvent: false});
          this.qualitySettingsInfoForm.disable({emitEvent: false});
        }

        this.generalForm.patchValue(this.stateConfigService.selectedProject);
        this.placeForm.patchValue(this.stateConfigService.selectedProject);
        this.timeForm.patchValue(this.stateConfigService.selectedProject);
        this.rememberScheduleValues(this.timeForm);
        this.project = this.stateConfigService.selectedProject;
        this.qualityValue = JSON.parse(this.stateConfigService.selectedProject.qualitySettings ?? "{}");

        this.rememberScheduleValues(this.builderInfoForm);
        this.updateScheduleDateConstraints(this.builderInfoForm);

        this.editImagePermission = this.hasEditPermission;

        if (this.addressInfoForm) {
          // vor dem Snapshot, damit der Default nicht als ungespeicherte Änderung zählt
          applyDefaultCountry(this.addressInfoForm.get('country'));
          this.onCountryChange(this.addressInfoForm.get('country')?.value);
          this.addressInfoForm.get('country')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(country => {
            this.onCountryChange(country);
          });
        }

        // Snapshot direkt nach Befüllen sichern
        this.snapshotInitialState();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveWith(navigate: boolean): void {
    const scheduleForm = this.create ? this.timeForm : this.builderInfoForm;
    if (!this.validateScheduleBeforeSave(scheduleForm)) {
      return;
    }

    if (this.create) {
      this.project = {...this.project, ...this.generalForm.value, ...this.placeForm.value, ...this.timeForm.value};
    } else {
      this.project = {...this.project, ...this.generalInfoForm.value, ...this.addressInfoForm.value, ...this.builderInfoForm.value};
      this.project.projectKind = this.qualityValue["usage"];
      this.project.qualitySettings = JSON.stringify(this.qualityValue);
    }

    if (this.create) {
      this.projectControllerService.updateProject(this.project, this.projectId).subscribe(project => {
        this.generalForm.markAsPristine();
        this.placeForm.markAsPristine();
        this.timeForm.markAsPristine();
        if (navigate) {
          void this.router.navigateByUrl("/projects");
        } else {
          this.project = project;
          this.generalForm.patchValue(project);
          this.placeForm.patchValue(project);
          this.timeForm.patchValue(project);
          this.notificationsService.showSuccessMessage("Änderungen gespeichert");
        }
      });
    } else {
      this.projectControllerService.updateProject(this.project, this.projectId).subscribe(project => {
        const savedProject = {
          ...project,
          projectKind: this.project.projectKind,
          qualitySettings: this.project.qualitySettings,
        };
        this.generalForm.markAsPristine();
        this.placeForm.markAsPristine();
        this.timeForm.markAsPristine();
        this.generalInfoForm.markAsPristine();
        this.generalInfoForm.markAsUntouched();
        this.addressInfoForm.markAsPristine();
        this.addressInfoForm.markAsUntouched();
        this.builderInfoForm.markAsPristine();
        this.builderInfoForm.markAsUntouched();
        this.qualitySettingsInfoForm.markAsPristine();
        this.qualitySettingsInfoForm.markAsUntouched();
        this.project = savedProject;
        this.stateConfigService.selectedProject = savedProject;
        this.snapshotInitialState();
        this.notificationsService.showSuccessMessage("Änderungen gespeichert");
      });
    }
  }

  openDeleteDialog() {
    this.dialog.open(ProjectConfigurationDeleteDialog, {
      panelClass:"rounded-corners-dialog",
      data: {project: this.project}
    });
  }

  public updateQualityValue(key: string, value: boolean | number | string | undefined): void {
    if (!this.hasEditPermission) return;
    this.qualityValue[key] = value;
    this.qualitySettingsInfoForm.markAsDirty();
  }

  validateNumberInput(value: string | number, parameterValue: string): void {
    if (!this.hasEditPermission) return;
    const str = String(value ?? '').trim();

    if (parameterValue === 'numberOfEmployees') {
      if (str === '') {
        this.qualitySettingsInfoForm.get('numberOfEmployees')?.setErrors(null);
        this.qualityValue[parameterValue] = undefined;
        this.qualitySettingsInfoForm.markAsDirty();
        return;
      }
      const numericValue = Number(str);
      this.qualitySettingsInfoForm.get('numberOfEmployees')?.setValue(numericValue);
      if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 10000) {
        this.qualitySettingsInfoForm.get('numberOfEmployees')?.setErrors({ range: true });
      } else {
        this.qualitySettingsInfoForm.get('numberOfEmployees')?.setErrors(null);
      }
      this.qualityValue[parameterValue] = numericValue;
      this.qualitySettingsInfoForm.markAsDirty();
      return;
    }

    this.qualityValue[parameterValue] = str === '' ? undefined : value;
    this.qualitySettingsInfoForm.markAsDirty();
  }

  /**
   * Text, den ein Dezimalfeld (FLOAT) anzeigt: die rohe Eingabe aus dem Control, sobald getippt
   * wurde (damit Zwischenstände wie „-" oder „-2," stehen bleiben), sonst der gespeicherte Wert
   * mit Komma als Dezimaltrennzeichen.
   */
  decimalInputText(key: string): string {
    const control = this.qualitySettingsInfoForm?.get(key);
    if (control?.dirty) return control.value ?? '';
    const stored = this.qualityValue[key];
    return stored === undefined || stored === null || stored === '' ? '' : String(stored).replace('.', ',');
  }

  /**
   * Dezimalfeld (z. B. Grundwasserspiegel): erlaubt Vorzeichen sowie Komma oder Punkt. Ungültige
   * Eingaben werden nicht still korrigiert, sondern als Fehler markiert und nicht übernommen.
   */
  validateDecimalInput(value: string | number | null | undefined, key: string): void {
    if (!this.hasEditPermission) return;
    const str = String(value ?? '').trim();

    let control = this.qualitySettingsInfoForm.get(key);
    if (!control) {
      // Felder aus dem Manifest, die das Formular nicht kennt; der Dirty-Vergleich
      // (qualityFormState) ist davon unabhängig, wann das Control angelegt wird.
      control = new UntypedFormControl('', [decimalInputValidator]);
      this.qualitySettingsInfoForm.addControl(key, control);
    } else if (!control.hasValidator(decimalInputValidator)) {
      control.addValidators(decimalInputValidator);
    }

    control.setValue(str);
    control.markAsDirty();
    if (control.valid) {
      this.qualityValue[key] = str === '' ? undefined : Number(str.replace(',', '.'));
    }
    this.qualitySettingsInfoForm.markAsDirty();
  }

  hasDecimalError(key: string): boolean {
    return !!this.qualitySettingsInfoForm?.get(key)?.hasError('decimal');
  }

  protected readonly Parameter = Parameter;
  protected readonly User = User;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faSpinnerThird = faSpinnerThird;
}

@Component({
  selector: 'projectSettings-deleteDialog',
  templateUrl: './project-settings-delete-dialog.html',
  styleUrls: ['./project-settings.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent
  ]
})
export class ProjectConfigurationDeleteDialog implements OnInit {
  data = inject<{
    project: Project;
  }>(MAT_DIALOG_DATA);
  private router = inject(Router);
  private projectControllerService = inject(ProjectControllerService);
  private dialogRef = inject<MatDialogRef<ProjectConfigurationDeleteDialog>>(MatDialogRef);
  private toastService = inject(BicToastService);

  @ViewChild(MatRipple) ripple!: MatRipple;

  deleteProject() {
    this.dialogRef.close();
    this.projectControllerService.deleteProject(this.data.project.id!).subscribe({
      next: () => void this.router.navigateByUrl("/projects"),
      error: err => this.toastService.error(serverErrorMessage(err, 'Das Projekt konnte nicht gelöscht werden.')),
    });
  }

  close() {
    this.dialogRef.close();
  }

  ngOnInit() {
  }
}
